import { useAuth0 } from '@auth0/auth0-react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import {
  GetListParameters,
  getList,
  getListMetadata,
  getListStatistics,
  updateCallingStatus,
} from '@/apis/lists';
import { ListSummary } from '@/types/shared';
import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import { CallingStatusValues } from '@/constants/callingStatus';

export const useListStatistics = ({
  listId,
  assignedToSub,
}: {
  listId: number;
  assignedToSub: string;
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  return useQuery({
    queryKey: ['list', listId, 'statistics', assignedToSub],
    queryFn: async () => {
      return getListStatistics({
        listId,
        assignedToSub,
        token: await getAccessTokenSilently(),
        domainCode,
      });
    },
  });
};

export const useList = (queryParams: Omit<GetListParameters, 'token' | 'domainCode'>) => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const { listId, offset, limit, assignedToSub, filters, sortColumn, sortOrder } = queryParams;
  return useQuery({
    queryKey: ['list', listId, offset, limit, assignedToSub, filters, sortColumn, sortOrder],
    queryFn: async () => {
      return getList({
        ...queryParams,
        token: await getAccessTokenSilently(),
        domainCode,
      });
    },
    placeholderData: keepPreviousData,
  });
};

export const useListMetadata = (listId: number) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['list', listId, 'metadata'],
    queryFn: async () =>
      getListMetadata({
        listId,
        token: await getAccessTokenSilently(),
      }),
    initialData: () => {
      const allListQueries = queryClient.getQueriesData<ListSummary[]>({
        queryKey: ['lists', user?.sub ?? ''],
      });
      const listData = allListQueries
        ?.reduce<
          ListSummary[]
        >((allLists, [, lists]) => (lists ? allLists.concat(lists) : allLists), [])
        .find((list) => list.id === listId);
      return listData;
    },
  });
};

export const useUpdateCallingStatus = ({
  listId,
  contactId,
}: {
  listId: number | undefined;
  contactId: number | undefined;
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const queryClient = useQueryClient();
  const userSub = user?.sub ?? '';
  return useMutation({
    mutationFn: async ({
      newCallingStatus,
      reassignSub,
    }: {
      newCallingStatus: string;
      reassignSub: boolean;
    }) => {
      if (!listId || !contactId) return;
      const callingStatusSetterSub = newCallingStatus !== CallingStatusValues[0] ? userSub : '';

      const token = await getAccessTokenSilently();
      await updateCallingStatus(token, listId, contactId, {
        assignedToSub: reassignSub ? userSub : undefined,
        callingStatus: newCallingStatus,
        callingStatusSetterSub,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['list', listId] });
    },
  });
};
